// Declarative registration of "what entity is this component displaying" with
// the Guru Cloud AI widget metadata (see utils/guruWidgetMetadata.ts).
//
// Components whose viewed entity lives in state (detail modals, mail panels)
// call this so widget submissions carry the email/order/thread in view even
// when the URL says nothing about it. Pass null while nothing is in view
// (e.g. modal closed); the registration clears automatically on unmount.

import { useEffect, useId } from 'react';
import {
  setGuruWidgetViewContext,
  clearGuruWidgetViewContext,
  type GuruWidgetViewContext,
} from '../utils/guruWidgetMetadata';

export function useGuruWidgetViewContext(key: string, ctx: GuruWidgetViewContext | null): void {
  // Instance-scoped registry key: the same component can be mounted twice
  // (e.g. EmailViewModal → SplitScreenModal → embedded EmailViewModal), and
  // one instance unmounting must not clear the other's registration.
  const instanceId = useId();
  const registryKey = `${key}#${instanceId}`;

  // Serialize so a fresh object with identical values doesn't churn the
  // registry on every render (callers build the context inline).
  const serialized = ctx === null ? null : JSON.stringify(ctx);

  useEffect(() => {
    if (serialized === null) {
      clearGuruWidgetViewContext(registryKey);
    } else {
      setGuruWidgetViewContext(registryKey, JSON.parse(serialized) as GuruWidgetViewContext);
    }
  }, [registryKey, serialized]);

  useEffect(() => {
    return () => {
      clearGuruWidgetViewContext(registryKey);
    };
  }, [registryKey]);
}
